// QRGenerator.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
    Snackbar,
    Alert,
    Card,
    CardContent,
    Divider,
    CircularProgress
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react'; // Updated import
import { v4 as uuidv4 } from 'uuid';
import { collection, addDoc, getDocs, doc, deleteDoc, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

// QR codes will expire after this many hours by default
const DEFAULT_EXPIRATION_HOURS = 8;

const QRGenerator = ({ orgId, userId }) => {
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [qrCodeData, setQrCodeData] = useState(null);
    const [expirationHours, setExpirationHours] = useState(DEFAULT_EXPIRATION_HOURS);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
    const [existingQRCodes, setExistingQRCodes] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchLocations();
        fetchExistingQRCodes();
    }, [orgId]);

    const fetchLocations = async () => {
        try {
            const locationsRef = collection(db, "organizations", orgId, "locations");
            const querySnapshot = await getDocs(locationsRef);

            const locationsList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setLocations(locationsList);

            // If there's at least one location, select it by default
            if (locationsList.length > 0 && !selectedLocation) {
                setSelectedLocation(locationsList[0].id);
            }

        } catch (error) {
            console.error("Error fetching locations:", error);
            setNotification({
                open: true,
                message: "Error loading locations: " + error.message,
                severity: "error"
            });
        }
    };

    const fetchExistingQRCodes = async () => {
        setLoading(true);
        try {
            const qrCodesRef = collection(db, "organizations", orgId, "qrCodes");
            const q = query(qrCodesRef, orderBy("createdAt", "desc"));
            const querySnapshot = await getDocs(q);

            const qrCodes = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                // Convert Firestore Timestamps to JS Date objects
                createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
                expiresAt: doc.data().expiresAt?.toDate?.() || new Date(doc.data().expiresAt)
            }));

            setExistingQRCodes(qrCodes);
        } catch (error) {
            console.error("Error fetching QR codes:", error);
            setNotification({
                open: true,
                message: "Error loading QR codes: " + error.message,
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const generateQRCode = () => {
        if (!selectedLocation) {
            setNotification({
                open: true,
                message: "Please select a location first",
                severity: "warning"
            });
            return;
        }

        // Find the selected location object
        const location = locations.find(loc => loc.id === selectedLocation);
        if (!location) return;

        // Generate a unique code ID
        const codeId = uuidv4();

        // Calculate expiration time
        const now = new Date();
        const expirationTime = new Date(now.getTime() + (expirationHours * 60 * 60 * 1000));

        // Create QR code data
        const qrData = {
            orgId: orgId,
            locationId: location.id,
            codeId: codeId,
            timestamp: now.getTime()
        };

        // Set the QR code data for display
        setQrCodeData({
            ...qrData,
            locationName: location.name,
            expiresAt: expirationTime
        });

        // Save to Firestore
        saveQRCodeToFirestore(qrData, location, expirationTime);
    };

    const saveQRCodeToFirestore = async (qrData, location, expirationTime) => {
        try {
            const qrCodesRef = collection(db, "organizations", orgId, "qrCodes");

            const docData = {
                codeId: qrData.codeId,
                locationId: location.id,
                locationName: location.name,
                createdAt: Timestamp.fromDate(new Date()),
                expiresAt: Timestamp.fromDate(expirationTime),
                createdBy: userId
            };

            await addDoc(qrCodesRef, docData);

            setNotification({
                open: true,
                message: "QR code generated successfully",
                severity: "success"
            });

            // Refresh the list of QR codes
            fetchExistingQRCodes();

        } catch (error) {
            console.error("Error saving QR code:", error);
            setNotification({
                open: true,
                message: "Error saving QR code: " + error.message,
                severity: "error"
            });
        }
    };

    const deleteQRCode = async (id) => {
        try {
            await deleteDoc(doc(db, "organizations", orgId, "qrCodes", id));

            // Update UI to remove the deleted code
            setExistingQRCodes(existingQRCodes.filter(code => code.id !== id));

            setNotification({
                open: true,
                message: "QR code deleted successfully",
                severity: "success"
            });
        } catch (error) {
            console.error("Error deleting QR code:", error);
            setNotification({
                open: true,
                message: "Error deleting QR code: " + error.message,
                severity: "error"
            });
        }
    };

    const downloadQRCode = () => {
        const canvas = document.getElementById("qr-code-canvas");
        if (!canvas) return;

        const url = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = url;
        link.download = `qr-code-${selectedLocation}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const closeNotification = () => {
        setNotification({ ...notification, open: false });
    };

    // Check if a QR code is expired
    const isExpired = (expiresAt) => {
        return new Date() > expiresAt;
    };

    return (
        <Box>
            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Generate Check-In QR Code
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id="location-select-label">Location</InputLabel>
                            <Select
                                labelId="location-select-label"
                                value={selectedLocation}
                                label="Location"
                                onChange={(e) => setSelectedLocation(e.target.value)}
                            >
                                {locations.length === 0 ? (
                                    <MenuItem disabled value="">
                                        No locations available
                                    </MenuItem>
                                ) : (
                                    locations.map(location => (
                                        <MenuItem key={location.id} value={location.id}>
                                            {location.name}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                            {locations.length === 0 && (
                                <FormHelperText>
                                    You need to add locations before generating QR codes
                                </FormHelperText>
                            )}
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Expiration (hours)"
                            type="number"
                            value={expirationHours}
                            onChange={(e) => setExpirationHours(Math.max(1, parseInt(e.target.value) || DEFAULT_EXPIRATION_HOURS))}
                            sx={{ mb: 2 }}
                            InputProps={{ inputProps: { min: 1 } }}
                            helperText="QR code will expire after this many hours"
                        />

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={generateQRCode}
                            disabled={!selectedLocation || locations.length === 0}
                            fullWidth
                            sx={{ mb: 2 }}
                        >
                            Generate QR Code
                        </Button>
                    </Grid>

                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        {qrCodeData ? (
                            <Box sx={{ textAlign: 'center' }}>
                                <QRCodeCanvas
                                    id="qr-code-canvas"
                                    value={JSON.stringify(qrCodeData)}
                                    size={200}
                                    level="H"
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    Location: {qrCodeData.locationName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Expires: {qrCodeData.expiresAt.toLocaleString()}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={downloadQRCode}
                                    sx={{ mt: 2 }}
                                >
                                    Download QR Code
                                </Button>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                QR code will appear here after generation
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* Existing QR Codes */}
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Existing QR Codes
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : existingQRCodes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
                        No QR codes have been generated yet
                    </Typography>
                ) : (
                    <Grid container spacing={2}>
                        {existingQRCodes.map(code => (
                            <Grid item xs={12} md={6} lg={4} key={code.id}>
                                <Card sx={{
                                    position: 'relative',
                                    opacity: isExpired(code.expiresAt) ? 0.7 : 1,
                                    bgcolor: isExpired(code.expiresAt) ? 'grey.100' : 'white'
                                }}>
                                    {isExpired(code.expiresAt) && (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: '10px',
                                            right: '10px',
                                            bgcolor: 'error.main',
                                            color: 'white',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            zIndex: 1
                                        }}>
                                            Expired
                                        </Box>
                                    )}
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {code.locationName}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                            <QRCodeCanvas
                                                value={JSON.stringify({
                                                    orgId: orgId,
                                                    locationId: code.locationId,
                                                    codeId: code.codeId,
                                                    timestamp: code.createdAt.getTime()
                                                })}
                                                size={150}
                                                level="H"
                                            />
                                        </Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Created: {code.createdAt.toLocaleString()}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Expires: {code.expiresAt.toLocaleString()}
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() => deleteQRCode(code.id)}
                                            sx={{ mt: 2 }}
                                            fullWidth
                                        >
                                            Delete
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Paper>

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={closeNotification}
            >
                <Alert
                    onClose={closeNotification}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default QRGenerator;